/**
 * Success Page
 * Booking confirmation success
 */

import { t, formatDate, formatCurrency } from '../i18n';
import { Card } from '../components';
import { getState } from '../store';
import { closeWindow, sendMessage } from '../liff';

/**
 * Render success page
 */
export function renderSuccessPage(): string {
  const state = getState();
  const { booking } = state;

  if (!booking) {
    return '';
  }

  // Calculate room rate (total minus extra charges)
  const extraChargeTotal = booking.extraCharges?.totalExtraCharge || 0;
  const roomRateTotal = booking.totalAmount - extraChargeTotal;

  return `
    <div class="success-page">
      <div class="success-header">
        <div class="success-icon">✓</div>
        <h1 class="success-title">${t('successTitle')}</h1>
        <p class="success-subtitle">${t('bookingConfirmed')}</p>
      </div>
      
      ${Card(
        `
        <div class="confirmation-box">
          <div class="confirmation-label">${t('confirmationNumber')}</div>
          <div class="confirmation-number">${booking.confirmationNumber}</div>
        </div>
        
        <div class="booking-details">
          <div class="detail-row">
            <span class="detail-icon-label">${booking.bookingType === 'SPA' ? '🌿 บริการ' : (booking.bookingType === 'RESTAURANT' ? '🍽️ บริการ' : '🏨 ประเภทห้องพัก')}</span>
            <span class="detail-value">${booking.roomTypeName}</span>
          </div>
          <div class="detail-row">
            <span class="detail-icon-label">👤 ${t('guestName')}</span>
            <span class="detail-value">${booking.guestName}</span>
          </div>
          ${
            booking.guestEmail
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">📧 ${t('email')}</span>
            <span class="detail-value">${booking.guestEmail}</span>
          </div>
          `
              : ''
          }
          <div class="detail-row">
            <span class="detail-icon-label">📅 ${booking.bookingType === 'SPA' || booking.bookingType === 'RESTAURANT' ? 'วันที่บริการ' : t('checkIn')}</span>
            <span class="detail-value">${formatDate(booking.checkIn)}</span>
          </div>
          ${
            booking.bookingType === 'SPA' && booking.spaDetails
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">⏰ รอบเวลา</span>
            <span class="detail-value">${booking.spaDetails.timeSlot} น. (${booking.spaDetails.durationMinutes} นาที)</span>
          </div>
          <div class="detail-row">
            <span class="detail-icon-label">👥 ผู้รับบริการ</span>
            <span class="detail-value">${booking.spaDetails.pax} ท่าน</span>
          </div>
          `
              : ''
          }
          ${
            booking.bookingType === 'RESTAURANT' && booking.restaurantDetails
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">⏰ รอบเวลา</span>
            <span class="detail-value">${booking.restaurantDetails.timeSlot} น.</span>
          </div>
          <div class="detail-row">
            <span class="detail-icon-label">🪑 โซนที่นั่ง</span>
            <span class="detail-value">${booking.restaurantDetails.seatingArea || 'Standard'} (${booking.restaurantDetails.partySize} ท่าน)</span>
          </div>
          `
              : ''
          }
          ${
            !booking.bookingType || booking.bookingType === 'ROOM'
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">📅 ${t('checkOut')}</span>
            <span class="detail-value">${formatDate(booking.checkOut)}</span>
          </div>
          <div class="detail-row">
            <span class="detail-icon-label">🌙 ${t('nights')}</span>
            <span class="detail-value">${booking.nights} ${t('nights')}</span>
          </div>
          <div class="detail-row">
            <span class="detail-icon-label">🚪 ${t('rooms')}</span>
            <span class="detail-value">${booking.rooms} ${t('rooms')}</span>
          </div>
          `
              : ''
          }
          ${
            booking.specialRequests
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">📝 ${t('specialRequests')}</span>
            <span class="detail-value">${booking.specialRequests}</span>
          </div>
          `
              : ''
          }
        </div>
        
        <div class="price-breakdown" style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e5e5e5;">
          ${
            booking.rooms > 1 && booking.ratePerRoom
              ? `<div class="detail-row">
                <span class="detail-icon-label">💵 ${t('roomRate')} (${booking.rooms} ${t('rooms')})</span>
                <span class="detail-value">${booking.rooms} × ${formatCurrency(booking.ratePerRoom, booking.currency)}</span>
              </div>`
              : `<div class="detail-row">
                <span class="detail-icon-label">💵 ${t('roomRate')}</span>
                <span class="detail-value">${formatCurrency(roomRateTotal, booking.currency)}</span>
              </div>`
          }
          ${
            booking.extraCharges && booking.extraCharges.totalExtraCharge > 0
              ? `
          <div class="detail-row">
            <span class="detail-icon-label">➕ ${t('extraCharges') || 'Extra Charges'}</span>
            <span class="detail-value">${formatCurrency(booking.extraCharges.totalExtraCharge, booking.currency)}</span>
          </div>
          <div class="extra-charges-detail" style="font-size: 12px; color: #666; padding-left: 24px; margin-top: -8px; margin-bottom: 8px;">
            ${booking.extraCharges.extraAdults > 0 ? `${booking.extraCharges.extraAdults} ${t('extraAdults') || 'extra adults'} × ${formatCurrency(booking.extraCharges.extraAdultCharge, booking.currency)}` : ''}
            ${booking.extraCharges.extraAdults > 0 && booking.extraCharges.extraChildren > 0 ? ', ' : ''}
            ${booking.extraCharges.extraChildren > 0 ? `${booking.extraCharges.extraChildren} ${t('extraChildren') || 'extra children'} × ${formatCurrency(booking.extraCharges.extraChildCharge, booking.currency)}` : ''}
          </div>
          `
              : ''
          }
          ${
            booking.totalBeforeTax
              ? (() => {
                  const roomRate = booking.totalBeforeTax;
                  const svc = Math.ceil(roomRate * 0.10);
                  const vat = Math.ceil(roomRate * 0.07);
                  return `
          <div style="border-top: 1px dashed #ddd; margin: 8px 0; padding-top: 8px;">
            <div class="detail-row" style="font-size: 13px; color: #888;">
              <span class="detail-icon-label">💵 ${t('roomRateBeforeTax')}</span>
              <span class="detail-value">${formatCurrency(roomRate, booking.currency)}</span>
            </div>
            <div class="detail-row" style="font-size: 13px; color: #888;">
              <span class="detail-icon-label">📊 ${t('serviceCharge10')}</span>
              <span class="detail-value">${formatCurrency(svc, booking.currency)}</span>
            </div>
            <div class="detail-row" style="font-size: 13px; color: #888;">
              <span class="detail-icon-label">📊 ${t('vat7')}</span>
              <span class="detail-value">${formatCurrency(vat, booking.currency)}</span>
            </div>
          </div>`;
                })()
              : ''
          }
          <div class="detail-row total-row">
            <span class="detail-icon-label">💰 ${t('totalAmount')}</span>
            <span class="detail-value-total">${formatCurrency(booking.totalAmount, booking.currency)}</span>
          </div>
        </div>
        
        <div class="check-times">
          <p>🕐 ${t('checkInTime')}</p>
          <p>🕐 ${t('checkOutTime')}</p>
        </div>
      `,
        'success-card'
      )}
      
      <div class="success-actions">
        <button 
          type="button" 
          class="btn btn-primary btn-block btn-lg"
          onclick="window.closeSuccessPage()"
        >
          ${t('done')}
        </button>
      </div>
    </div>
  `;
}

/**
 * Initialize success page event handlers
 */
export function initSuccessPage(): void {
  const state = getState();

  // Close success page and send confirmation to LINE chat
  window.closeSuccessPage = async () => {
    const { booking } = state;

    if (booking) {
      // Create Flex Message with success page design
      const flexMessage = {
        type: 'flex',
        altText: `✅ ${t('successTitle')} - ${booking.confirmationNumber}`,
        contents: {
          type: 'bubble',
          styles: {
            header: {
              backgroundColor: '#06C755',
            },
          },
          header: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'text',
                    text: '✓',
                    size: '3xl',
                    weight: 'bold',
                    color: '#ffffff',
                    align: 'center',
                  },
                ],
                width: '60px',
                height: '60px',
                backgroundColor: '#00B900',
                cornerRadius: '30px',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 'none',
                offsetTop: '10px',
              },
              {
                type: 'text',
                text: t('successTitle'),
                weight: 'bold',
                size: 'xl',
                color: '#ffffff',
                align: 'center',
                margin: 'md',
              },
              {
                type: 'text',
                text: t('bookingConfirmed'),
                size: 'sm',
                color: '#ffffff',
                align: 'center',
                margin: 'xs',
              },
            ],
            paddingAll: 'xl',
            alignItems: 'center',
          },
          body: {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'text',
                    text: t('confirmationNumber'),
                    size: 'sm',
                    color: '#999999',
                    align: 'center',
                  },
                  {
                    type: 'text',
                    text: booking.confirmationNumber,
                    size: 'xl',
                    weight: 'bold',
                    color: '#00B900',
                    align: 'center',
                    margin: 'xs',
                  },
                ],
                backgroundColor: '#E8F5E9',
                cornerRadius: 'md',
                paddingAll: 'md',
                margin: 'none',
              },
              {
                type: 'separator',
                margin: 'xl',
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '🏨',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('roomType'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: booking.roomTypeName,
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  ...(booking.ratePlanName
                    ? [
                        {
                          type: 'box' as const,
                          layout: 'baseline' as const,
                          contents: [
                            {
                              type: 'text' as const,
                              text: '📋',
                              size: 'sm' as const,
                              flex: 0,
                            },
                            {
                              type: 'text' as const,
                              text: t('ratePlan'),
                              size: 'sm' as const,
                              color: '#666666',
                              flex: 2,
                              margin: 'sm' as const,
                            },
                            {
                              type: 'text' as const,
                              text: booking.ratePlanName,
                              size: 'sm' as const,
                              color: '#111111',
                              align: 'end' as const,
                              flex: 3,
                            },
                          ],
                          margin: 'md' as const,
                        },
                      ]
                    : []),
                  ...(booking.isRefundable === false
                    ? [
                        {
                          type: 'box' as const,
                          layout: 'baseline' as const,
                          contents: [
                            {
                              type: 'text' as const,
                              text: '⛔',
                              size: 'sm' as const,
                              flex: 0,
                            },
                            {
                              type: 'text' as const,
                              text: t('nonRefundable'),
                              size: 'sm' as const,
                              color: '#dc2626',
                              weight: 'bold' as const,
                              flex: 5,
                              margin: 'sm' as const,
                            },
                          ],
                          margin: 'md' as const,
                        },
                      ]
                    : []),
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '👤',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('guestName'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: booking.guestName,
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '📅',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('checkIn'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: formatDate(booking.checkIn),
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '📅',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('checkOut'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: formatDate(booking.checkOut),
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '🌙',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('nights'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: `${booking.nights} ${t('nights')}`,
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '🚪',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('rooms'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: `${booking.rooms} ${t('rooms')}`,
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '👥',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('guests'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: `${booking.adults} ${t('adults')}${booking.children > 0 ? `, ${booking.children} ${t('children')}` : ''}`,
                        size: 'sm',
                        color: '#111111',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  ...(booking.extraCharges && booking.extraCharges.totalExtraCharge > 0
                    ? [
                        {
                          type: 'box' as const,
                          layout: 'baseline' as const,
                          contents: [
                            {
                              type: 'text' as const,
                              text: '➕',
                              size: 'sm' as const,
                              flex: 0,
                            },
                            {
                              type: 'text' as const,
                              text: t('extraCharges'),
                              size: 'sm' as const,
                              color: '#666666',
                              flex: 2,
                              margin: 'sm' as const,
                            },
                            {
                              type: 'text' as const,
                              text: formatCurrency(
                                booking.extraCharges.totalExtraCharge,
                                booking.currency
                              ),
                              size: 'sm' as const,
                              color: '#FF6B00',
                              align: 'end' as const,
                              flex: 3,
                            },
                          ],
                          margin: 'md' as const,
                        },
                      ]
                    : []),
                  // Price breakdown (when totalBeforeTax is available)
                  ...(booking.totalBeforeTax
                    ? (() => {
                        const roomRate = booking.totalBeforeTax;
                        const svc = Math.ceil(roomRate * 0.10);
                        const vat = Math.ceil(roomRate * 0.07);
                        return [
                          {
                            type: 'separator' as const,
                            margin: 'md' as const,
                          },
                          {
                            type: 'box' as const,
                            layout: 'baseline' as const,
                            contents: [
                              { type: 'text' as const, text: '💵', size: 'sm' as const, flex: 0 },
                              { type: 'text' as const, text: t('roomRateBeforeTax'), size: 'xs' as const, color: '#888888', flex: 3, margin: 'sm' as const },
                              { type: 'text' as const, text: formatCurrency(roomRate, booking.currency), size: 'xs' as const, color: '#888888', align: 'end' as const, flex: 3 },
                            ],
                            margin: 'md' as const,
                          },
                          {
                            type: 'box' as const,
                            layout: 'baseline' as const,
                            contents: [
                              { type: 'text' as const, text: '📊', size: 'sm' as const, flex: 0 },
                              { type: 'text' as const, text: t('serviceCharge10'), size: 'xs' as const, color: '#888888', flex: 3, margin: 'sm' as const },
                              { type: 'text' as const, text: formatCurrency(svc, booking.currency), size: 'xs' as const, color: '#888888', align: 'end' as const, flex: 3 },
                            ],
                            margin: 'xs' as const,
                          },
                          {
                            type: 'box' as const,
                            layout: 'baseline' as const,
                            contents: [
                              { type: 'text' as const, text: '📊', size: 'sm' as const, flex: 0 },
                              { type: 'text' as const, text: t('vat7'), size: 'xs' as const, color: '#888888', flex: 3, margin: 'sm' as const },
                              { type: 'text' as const, text: formatCurrency(vat, booking.currency), size: 'xs' as const, color: '#888888', align: 'end' as const, flex: 3 },
                            ],
                            margin: 'xs' as const,
                          },
                        ];
                      })()
                    : []),
                  {
                    type: 'box',
                    layout: 'baseline',
                    contents: [
                      {
                        type: 'text',
                        text: '💰',
                        size: 'sm',
                        flex: 0,
                      },
                      {
                        type: 'text',
                        text: t('totalAmount'),
                        size: 'sm',
                        color: '#666666',
                        flex: 2,
                        margin: 'sm',
                      },
                      {
                        type: 'text',
                        text: formatCurrency(booking.totalAmount, booking.currency),
                        size: 'md',
                        weight: 'bold',
                        color: '#00B900',
                        align: 'end',
                        flex: 3,
                      },
                    ],
                    margin: 'md',
                  },
                  ...(booking.paymentStatus && booking.paymentStatus !== 'pending' ? [{
                    type: 'box' as const,
                    layout: 'baseline' as const,
                    contents: [
                      { type: 'text' as const, text: booking.paymentStatus === 'paid' ? '💳' : '🏨', size: 'sm' as const, flex: 0 },
                      { type: 'text' as const, text: t('paymentTitle'), size: 'sm' as const, color: '#666666', flex: 2, margin: 'sm' as const },
                      { type: 'text' as const, text: booking.paymentStatus === 'paid' ? (booking.paymentMethodDisplay || t('statusPaid')) : t('skipPayment'), size: 'sm' as const, color: booking.paymentStatus === 'paid' ? '#00B900' : '#1890ff', weight: 'bold' as const, align: 'end' as const, flex: 3 },
                    ],
                    margin: 'md' as const,
                  }] : []),
                ],
                margin: 'md',
              },
              {
                type: 'separator',
                margin: 'xl',
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'text',
                    text: `🕐 ${t('checkInTime')}`,
                    size: 'xs',
                    color: '#666666',
                    margin: 'sm',
                  },
                  {
                    type: 'text',
                    text: `🕐 ${t('checkOutTime')}`,
                    size: 'xs',
                    color: '#666666',
                    margin: 'sm',
                  },
                ],
                margin: 'md',
              },
            ],
            paddingAll: 'xl',
          },
        },
      };

      // Send Flex Message to chat
      try {
        await sendMessage(flexMessage);
      } catch (error) {
        console.error('[Success] Failed to send message:', error);
        // Don't block closing the window if message fails
      }
    }
    closeWindow();
  };
}

// Extend window for global handlers
declare global {
  interface Window {
    closeSuccessPage: () => void;
  }
}
